import _ from 'lodash';
import moment from 'moment';
import { ANNOTATION_TYPE } from '@/shared/ui';

/**
 * Converts stored annotation timestamps on an ECG strip into sample offsets for rendering. Deduplicates the draw list, then for sample marks, patterns, BCC calipers, text and line widgets turns each recorded time into a sample index relative to the strip start at the given sampling frequency, and captures the sample mark's start offset when it falls inside the strip window.
 *
 * @param drawList - Stored annotation widgets carrying ISO times to convert.
 * @param startStripEpoch - Epoch milliseconds of the strip's first sample.
 * @param samplingFrequency - Samples per second of the ECG signal.
 * @param stopStripEpoch - Epoch milliseconds of the strip's last sample.
 * @returns An object carrying the converted draw list and the sample offset of the in-window sample mark.
 */
export const formatDrawDisplay = (
  drawList: any[],
  startStripEpoch: number,
  samplingFrequency: number,
  stopStripEpoch: number,
) => {
  let startSample;
  // pre-existing bug: _.isEqual() is invoked (passing its boolean result as the comparator), not passed by reference
  const draw = _.map(_.uniqWith(drawList, (_.isEqual as any)()), (d) => {
    if (d.type === ANNOTATION_TYPE.SAMPLE_MARK) {
      const startSampleEpoch = moment(d.startTime).valueOf();
      const startSampleMark = d.startTime
        ? Math.round(((startSampleEpoch - startStripEpoch) / 1000) * samplingFrequency)
        : d.startSample;
      const endSample = d.endTime
        ? Math.round(((moment(d.endTime).valueOf() - startStripEpoch) / 1000) * samplingFrequency)
        : d.endSample;
      if (startSampleEpoch >= startStripEpoch && startSampleEpoch <= stopStripEpoch) {
        startSample = startSampleMark;
      }
      return {
        ...d,
        startSample: startSampleMark,
        endSample,
        sampleMarkRange: endSample - startSampleMark,
      };
    }
    if (d.type === ANNOTATION_TYPE.PATTERN) {
      const startSample = d.startTime
        ? Math.round(((moment(d.startTime).valueOf() - startStripEpoch) / 1000) * samplingFrequency)
        : d.startSample;
      const endSample = d.endTime
        ? Math.round(((moment(d.endTime).valueOf() - startStripEpoch) / 1000) * samplingFrequency)
        : d.endSample;
      return { ...d, startSample, endSample };
    }
    if (d.type === ANNOTATION_TYPE.BCC) {
      const { xr } = d || {};
      const newXr = {};
      if (!_.isEmpty(d.xrTime)) {
        _.forEach(Object.keys(d.xrTime), (key) => {
          const time = d.xrTime[key];
          _.assign(newXr, {
            [key]: Math.round(
              ((moment(time).valueOf() - startStripEpoch) / 1000) * samplingFrequency,
            ),
          });
        });
      }

      return { ...d, xr: d.xrTime ? newXr : xr };
    }
    if (
      d.type === ANNOTATION_TYPE.TEXT ||
      d.type === ANNOTATION_TYPE.HLINE ||
      d.type === ANNOTATION_TYPE.VLINE
    ) {
      let { xr } = d;
      if (d.xrTime) {
        xr = ((moment(d.xrTime).valueOf() - startStripEpoch) / 1000) * samplingFrequency;
      }
      return { ...d, xr };
    }
    return d;
  });
  return { draw, startSample };
};
